import { 
  collection, 
  doc,
  getDoc, 
  getDocs, 
  addDoc,
  updateDoc,
  deleteDoc,
  query, 
  where, 
  orderBy,
  limit as limitQuery,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Alert Service
 * CRUD operations for risk alerts
 */
class AlertService {
  constructor() {
    this.collectionName = 'risk_alerts';
  }
  
  /**
   * Get all alerts
   */
  async getAll() {
    try {
      const alertsRef = collection(db, this.collectionName);
      const q = query(alertsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting alerts:', error);
      throw error;
    }
  }
  
  /**
   * Get alert by ID
   */
  async getById(id) {
    try {
      const docRef = doc(db, this.collectionName, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        };
      }
      
      return null;
    } catch (error) {
      console.error(`Error getting alert ${id}:`, error);
      throw error;
    }
  }
  
  /**
   * Get active alerts (not dismissed or resolved)
   */
  async getActive() {
    try {
      const alertsRef = collection(db, this.collectionName);
      const q = query(
        alertsRef, 
        where('status', '==', 'ACTIVE'),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting active alerts:', error);
      throw error;
    }
  }
  
  /**
   * Get alerts for a specific user
   */
  async getByUser(userId) {
    try {
      const alertsRef = collection(db, this.collectionName);
      const q = query(
        alertsRef,
        where('recipientUsers', 'array-contains', userId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error(`Error getting alerts for user ${userId}:`, error);
      throw error;
    }
  }
  
  /**
   * Get alerts by severity
   */
  async getBySeverity(severity) {
    try {
      const alertsRef = collection(db, this.collectionName);
      const q = query(
        alertsRef,
        where('severity', '==', severity),
        where('status', '==', 'ACTIVE'),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error(`Error getting ${severity} alerts:`, error);
      throw error;
    }
  }
  
  /**
   * Get unread alerts for a user
   */
  async getUnread(userId) {
    try {
      const alertsRef = collection(db, this.collectionName);
      const q = query(
        alertsRef,
        where('recipientUsers', 'array-contains', userId),
        where('status', '==', 'ACTIVE'),
        where('read', '==', false),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error(`Error getting unread alerts for user ${userId}:`, error);
      // If index doesn't exist yet, fall back to client-side filtering
      const allAlerts = await this.getByUser(userId);
      return allAlerts.filter(alert => 
        alert.status === 'ACTIVE' && alert.read === false
      );
    }
  }
  
  /**
   * Get alert count by severity for a user
   */
  async getCountBySeverity(userId) {
    try {
      const alerts = await this.getByUser(userId);
      const activeAlerts = alerts.filter(a => a.status === 'ACTIVE');
      
      return {
        critical: activeAlerts.filter(a => a.severity === 'CRITICAL').length,
        high: activeAlerts.filter(a => a.severity === 'HIGH').length,
        medium: activeAlerts.filter(a => a.severity === 'MEDIUM').length,
        total: activeAlerts.length,
        unread: activeAlerts.filter(a => !a.read).length
      };
    } catch (error) {
      console.error('Error getting alert counts:', error);
      return { critical: 0, high: 0, medium: 0, total: 0, unread: 0 };
    }
  }
  
  /**
   * Create a new alert
   */
  async create(alertData) {
    try {
      const alertsRef = collection(db, this.collectionName);
      const docRef = await addDoc(alertsRef, {
        ...alertData,
        timestamp: serverTimestamp(),
        createdAt: new Date().toISOString(),
        status: 'ACTIVE',
        read: false,
        acknowledgedBy: []
      });
      
      return {
        id: docRef.id,
        ...alertData
      };
    } catch (error) {
      console.error('Error creating alert:', error);
      throw error;
    }
  }
  
  /**
   * Mark alert as read
   */
  async markAsRead(alertId, userId) {
    try {
      const docRef = doc(db, this.collectionName, alertId);
      await updateDoc(docRef, {
        read: true,
        readBy: userId,
        readAt: serverTimestamp()
      });
      
      return true;
    } catch (error) {
      console.error(`Error marking alert ${alertId} as read:`, error);
      throw error;
    }
  }
  
  /**
   * Acknowledge an alert
   */
  async acknowledge(alertId, userId, userName) {
    try {
      const alert = await this.getById(alertId);
      const acknowledgedBy = alert.acknowledgedBy || [];
      
      // Add user to acknowledged list if not already there
      if (!acknowledgedBy.some(ack => ack.userId === userId)) {
        acknowledgedBy.push({
          userId,
          userName,
          acknowledgedAt: new Date().toISOString()
        });
      }
      
      const docRef = doc(db, this.collectionName, alertId);
      await updateDoc(docRef, {
        acknowledgedBy,
        acknowledgedAt: serverTimestamp()
      });
      
      return true;
    } catch (error) {
      console.error(`Error acknowledging alert ${alertId}:`, error);
      throw error;
    }
  }
  
  /**
   * Dismiss an alert
   */
  async dismiss(alertId, userId, reason = '') {
    try {
      const docRef = doc(db, this.collectionName, alertId);
      await updateDoc(docRef, {
        status: 'DISMISSED',
        dismissedBy: userId,
        dismissedAt: serverTimestamp(),
        dismissalReason: reason
      });
      
      return true;
    } catch (error) {
      console.error(`Error dismissing alert ${alertId}:`, error);
      throw error;
    }
  }
  
  /**
   * Resolve an alert
   */
  async resolve(alertId, userId, resolution = '') {
    try {
      const docRef = doc(db, this.collectionName, alertId);
      await updateDoc(docRef, {
        status: 'RESOLVED',
        resolvedBy: userId,
        resolvedAt: serverTimestamp(),
        resolution
      });
      
      return true;
    } catch (error) {
      console.error(`Error resolving alert ${alertId}:`, error);
      throw error;
    }
  }
  
  /**
   * Escalate an alert
   */
  async escalate(alertId, escalatedTo, escalationReason = '') {
    try {
      const docRef = doc(db, this.collectionName, alertId);
      await updateDoc(docRef, {
        escalated: true,
        escalatedTo,
        escalatedAt: serverTimestamp(),
        escalationReason
      });
      
      return true;
    } catch (error) {
      console.error(`Error escalating alert ${alertId}:`, error);
      throw error;
    }
  }
  
  /**
   * Delete an alert (admin only)
   */
  async delete(id) {
    try {
      const docRef = doc(db, this.collectionName, id);
      await deleteDoc(docRef);
      return true;
    } catch (error) {
      console.error(`Error deleting alert ${id}:`, error);
      throw error;
    }
  }
  
  /**
   * Clean up old resolved/dismissed alerts
   */
  async cleanup(daysOld = 30) {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysOld);
      
      const alertsRef = collection(db, this.collectionName);
      const q = query(
        alertsRef,
        where('status', 'in', ['RESOLVED', 'DISMISSED']),
        where('createdAt', '<', cutoffDate.toISOString())
      );
      
      const snapshot = await getDocs(q);
      
      const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      
      console.log(`🧹 Cleaned up ${snapshot.docs.length} old alerts`);
      return snapshot.docs.length;
    } catch (error) {
      console.error('Error cleaning up old alerts:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const alertService = new AlertService();
export default alertService;

